import { useCallback, useEffect, useState } from "react"

import { getRetrievalEngines } from "@/lib/api"
import type { RetrievalEngine } from "@/types/api"

const ENGINE_KEY = "celver-copilot:retrieval-engine"

/**
 * The SharePoint retrieval engine catalog plus the user's pick, persisted per
 * browser — same shape as useModels.
 *
 * `selectedId` stays null until the catalog confirms the stored engine is one
 * this deployment can run: a deployment that later turns Copilot off must not
 * keep receiving "copilot" from browsers that remember it. The server also
 * falls back on its own, so a stale value degrades quietly either way.
 */
export function useRetrievalEngine() {
  const [engines, setEngines] = useState<RetrievalEngine[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    getRetrievalEngines()
      .then(({ engines: available, default: serverDefault }) => {
        if (cancelled) return
        setEngines(available)
        const stored = localStorage.getItem(ENGINE_KEY)
        const known = available.some((e) => e.id === stored)
        if (stored && !known) localStorage.removeItem(ENGINE_KEY)
        setSelectedId(known ? stored : serverDefault)
      })
      .catch(() => {
        // The switch is an enhancement — chat falls back to the server default
      })
    return () => {
      cancelled = true
    }
  }, [])

  const selectEngine = useCallback((id: string) => {
    localStorage.setItem(ENGINE_KEY, id)
    setSelectedId(id)
  }, [])

  return { engines, selectedId, selectEngine }
}
